/**
 * PDF生成関連のAPI実装
 *
 * OpenAPI仕様準拠の実装:
 * 1. POST /api/v1/pdf/resume/generate/ でトークンを取得
 * 2. GET /api/v1/pdf/resume/{token} でPDFをダウンロード
 */

import { createAuthHeaders } from '@src/api/core/authHeaders';
import { httpClient } from '@src/api/core/http-client';
import { downloadPdfBlob } from '@src/api/core/pdfDownloadUtils';
import { getApiConfig } from '@src/config/api';
import { API_ENDPOINTS } from '@src/constants/apiEndpoints';
import { PdfGenerationError } from '@src/errors';
import {
  buildPdfGenerationRequest,
  type PdfGenerationRequest,
} from '@src/modules/pdf/pdfRequestMapper';
import { getAccessToken } from '@src/modules/security/authHelpers';
import { errorLogger } from '@src/services/errorLogger';
import {
  getAllErrorMessages,
  getErrorsByField,
  hasMultipleErrors,
} from '@src/types/shared/api/common';
import type { GeneratePdfApiParams } from '@src/types/shared/api/pdf_api_params';
import type {
  PdfGenerateApiResponse,
  PdfGenerateSuccessResponse,
} from '@src/types/shared/api/pdf_api_response';
import { isPdfGenerateSuccess } from '@src/types/shared/api/pdf_api_response';
import { getResumePdfDownloadUrl } from '@src/utils/pdfDownloadUrl';
import { generateResumePdfFileName } from '@src/utils/pdfFileName';

/**
 * PDFトークンを生成（OpenAPI仕様準拠）
 *
 * API定義に従い、POST /api/v1/pdf/resume/generate/ を呼び出して
 * PDFダウンロード用のトークンを取得します。
 *
 * @param params PDF生成パラメータ
 * @returns PDF生成レスポンス（トークン、有効期限、ダウンロードURLを含む）
 * @throws {PdfGenerationError} PDF生成に失敗した場合
 */
async function generatePdfToken(params: PdfGenerationRequest): Promise<PdfGenerateSuccessResponse> {
  const token = getAccessToken();

  // 認証が不要なエンドポイントの場合、警告のみ表示
  if (!token) {
    console.warn('認証トークンがありません。匿名でPDF生成を試みます。');
  }

  try {
    const response = await httpClient.post<PdfGenerateApiResponse>(
      API_ENDPOINTS.PDF.GENERATE_RESUME.replace(/^\//, ''),
      params,
      {
        headers: createAuthHeaders({ throwOnMissing: false }),
      }
    );

    // レスポンスが成功かチェック
    if (!isPdfGenerateSuccess(response)) {
      // 複数エラーの場合の処理
      const isMultiple = hasMultipleErrors(response);
      const allMessages = getAllErrorMessages(response);
      const errorsByField = getErrorsByField(response);

      // エラーメッセージを構築
      const errorMessage = isMultiple
        ? `${response.error.message}\n${allMessages.slice(1).join('\n')}`
        : response.error.message;

      // 詳細なコンテキスト情報
      const errorContext = {
        params,
        response,
        ...(isMultiple && { errorsByField }),
      };

      throw new PdfGenerationError(errorMessage, errorContext);
    }

    return response;
  } catch (error) {
    const pdfError = new PdfGenerationError(
      error instanceof Error ? error.message : 'PDF生成に失敗しました',
      { params },
      { cause: error }
    );

    await errorLogger.logError(pdfError, {
      context: 'PDF生成',
      action: 'generatePdfToken',
      params: JSON.stringify(params),
    });

    throw pdfError;
  }
}

/**
 * PDFトークンからBlobをダウンロード
 *
 * API定義に従い、GET /api/v1/pdf/resume/{token} を呼び出して
 * PDFファイルをダウンロードします。
 *
 * @param token PDF取得用トークン
 * @returns PDFファイルのBlob
 * @throws {Error} PDFダウンロードに失敗した場合
 */
async function downloadPdfFromToken(token: string): Promise<Blob> {
  try {
    const pdfPath = `api/v1/pdf/resume/${token}`;

    return await httpClient.getBlob(pdfPath);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'PDFダウンロードに失敗しました';
    await errorLogger.logError(new Error(errorMessage), {
      context: 'PDFダウンロード',
      action: 'downloadPdfFromToken',
      token,
    });
    throw new Error(errorMessage);
  }
}

/**
 * PDFをダウンロード（2ステップフロー）
 *
 * OpenAPI仕様に従い、以下の手順でPDFをダウンロードします:
 * 1. PDFトークンを生成
 * 2. トークンを使ってPDFをダウンロード
 * 3. ブラウザでダウンロードを実行
 *
 * @param params PDF生成パラメータ
 * @throws {Error} PDF生成またはダウンロードに失敗した場合
 */
export async function downloadPdf(params: GeneratePdfApiParams): Promise<void> {
  const apiConfig = getApiConfig();

  if (apiConfig.isMockMode) {
    try {
      // モック用の遅延処理（2秒）
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const downloadUrl = getResumePdfDownloadUrl();

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`モックPDFの取得に失敗しました（${response.status}）`);
      }

      const blob = await response.blob();
      const fileName = generateResumePdfFileName();
      downloadPdfBlob(blob, fileName);
      return;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'PDFのダウンロードに失敗しました';
      console.error('PDFダウンロードエラー (mock mode):', error);
      throw new Error(errorMessage);
    }
  }

  try {
    const requestParams = buildPdfGenerationRequest(params);

    // 1. PDFトークンを生成
    const response = await generatePdfToken(requestParams);

    // 2. トークンを使ってPDFをダウンロード
    const blob = await downloadPdfFromToken(response.data.token);

    // 3. ブラウザでダウンロードを実行
    const fileName = generateResumePdfFileName();
    downloadPdfBlob(blob, fileName);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'PDFのダウンロードに失敗しました';
    console.error('PDFダウンロードエラー:', error);
    throw new Error(errorMessage);
  }
}
